import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useAuth } from '../../contexts/AuthContext';
import { pipelineService } from '../../services/pipelineService';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';

// Updated components with job-specific functionality
import JobSelector from './components/JobSelector';
import PipelineOverview from './components/PipelineOverview';
import JobPipelineAnalytics from './components/JobPipelineAnalytics';
import PipelineStage from './components/PipelineStage';
import PipelineFilters from './components/PipelineFilters';
import BulkActions from './components/BulkActions';
import CandidateModal from './components/CandidateModal';

/**
 * Redesigned Candidate Pipeline - Job-specific with modern HR SaaS styling
 * Features: Job selection, per-job pipelines, aggregated overview, analytics
 */
const CandidatePipeline = () => {
  const { user } = useAuth();
  
  // Core state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  
  // Analytics data
  const [jobMetrics, setJobMetrics] = useState({});
  const [overviewData, setOverviewData] = useState({});
  const [selectedJobAnalytics, setSelectedJobAnalytics] = useState({});
  
  // Filters and search
  const [filters, setFilters] = useState({});
  const [isTouch, setIsTouch] = useState(false);
  const [activeStageTab, setActiveStageTab] = useState('new');

  // Pipeline stages configuration
  const stages = [
    { id: 'new', name: 'Applied', order: 1, color: 'bg-blue-500' },
    { id: 'screening', name: 'Screening', order: 2, color: 'bg-amber-500' },
    { id: 'interview', name: 'Interview', order: 3, color: 'bg-purple-500' },
    { id: 'final-review', name: 'Final Review', order: 4, color: 'bg-indigo-500' },
    { id: 'offer', name: 'Offer', order: 5, color: 'bg-emerald-500' },
    { id: 'hired', name: 'Hired', order: 6, color: 'bg-green-600' },
    { id: 'rejected', name: 'Rejected', order: 7, color: 'bg-red-500' }
  ];

  // Touch detection
  useEffect(() => {
    const isTouchCapable =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia?.('(pointer: coarse)')?.matches;
    setIsTouch(!!isTouchCapable);
  }, []);

  // Load jobs on component mount
  useEffect(() => {
    if (!user) return;
    loadJobs();
  }, [user]);

  // Load applications when job selected
  useEffect(() => {
    if (selectedJob) {
      loadApplicationsForJob(selectedJob?.id);
      loadJobAnalytics(selectedJob?.id);
    } else {
      setApplications([]);
      setSelectedJobAnalytics({});
    }
  }, [selectedJob]);

  // Load aggregated overview data
  useEffect(() => {
    if (jobs?.length > 0) {
      loadOverviewData();
    }
  }, [jobs, jobMetrics]);

  // Data loading functions
  const loadJobs = async () => {
    try {
      setLoading(true);
      const jobsData = await pipelineService?.getJobs();
      setJobs(jobsData || []);
      
      // Load metrics for each job
      const metricsPromises = jobsData?.map(job => 
        pipelineService?.getJobPipelineAnalytics(job?.id)
      ) || [];
      
      const metricsResults = await Promise.allSettled(metricsPromises);
      const metricsMap = {};
      
      metricsResults?.forEach((result, index) => {
        if (result?.status === 'fulfilled' && jobsData?.[index]) {
          metricsMap[jobsData[index].id] = result?.value;
        }
      });
      
      setJobMetrics(metricsMap);
      
      // Auto-select first job if none selected
      if (!selectedJob && jobsData?.length > 0) {
        setSelectedJob(jobsData?.[0]);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplicationsForJob = async (jobId) => {
    try {
      setApplicationsLoading(true);
      const applicationsData = await pipelineService?.getApplicationsForJob(jobId);
      setApplications(applicationsData || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setApplications([]);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const loadJobAnalytics = async (jobId) => {
    try {
      const analytics = await pipelineService?.getJobPipelineAnalytics(jobId);
      setSelectedJobAnalytics(analytics || {});
    } catch (error) {
      console.error('Error loading job analytics:', error);
      setSelectedJobAnalytics({});
    }
  };

  const loadOverviewData = () => {
    const allMetrics = Object.values(jobMetrics);
    
    const totalJobs = jobs?.length || 0;
    const totalActiveJobs = jobs?.filter(job => job?.status === 'open')?.length || 0;
    const totalApplications = allMetrics?.reduce((sum, metrics) => sum + (metrics?.totalApplications || 0), 0);
    
    const averageTimeToHire = allMetrics?.length > 0 ? 
      allMetrics?.reduce((sum, metrics) => sum + (metrics?.averageTimeToHire || 0), 0) / allMetrics?.length : 0;
    
    const overallConversionRate = totalApplications > 0 ? 
      allMetrics?.reduce((sum, metrics) => {
        const hired = metrics?.stageDistribution?.hired || 0;
        return sum + hired;
      }, 0) / totalApplications * 100 : 0;

    // Aggregate stage distribution
    const stageDistribution = {};
    stages?.forEach(stage => {
      stageDistribution[stage.id] = allMetrics?.reduce((sum, metrics) => 
        sum + (metrics?.stageDistribution?.[stage?.id] || 0), 0
      );
    });

    // Remove sample bottlenecks - use empty array
    const topBottlenecks = [];

    setOverviewData({
      totalJobs,
      totalActiveJobs,
      totalApplications,
      averageTimeToHire,
      overallConversionRate,
      topBottlenecks,
      stageDistribution,
      recentTrends: {
        applications: 0 // No mock data
      }
    });
  };

  // Filter applications
  const filteredApplications = useMemo(() => {
    if (!applications?.length) return [];
    
    const term = (filters?.search || '')?.trim()?.toLowerCase();
    
    return applications?.filter((app) => {
      if (term) {
        const matchesText =
          app?.name?.toLowerCase()?.includes(term) ||
          app?.email?.toLowerCase()?.includes(term) ||
          app?.location?.toLowerCase()?.includes(term);
        if (!matchesText) return false;
      }

      if (filters?.source && filters?.source !== 'all') {
        if (app?.source !== filters?.source) return false;
      }

      if (filters?.stage && filters?.stage !== 'all') {
        if (app?.stage !== filters?.stage) return false;
      }

      return true;
    });
  }, [applications, filters]);

  // Group applications by stage
  const applicationsByStage = useMemo(() => {
    const grouped = Object.fromEntries(stages?.map(s => [s?.id, []]));
    for (const app of filteredApplications) {
      if (grouped?.[app?.stage]) {
        grouped?.[app?.stage]?.push(app);
      }
    }
    return grouped;
  }, [filteredApplications, stages]);

  const getApplicationsByStage = useCallback(
    (stageId) => applicationsByStage?.[stageId] || [],
    [applicationsByStage]
  );

  // Event handlers
  const handleJobSelect = useCallback((job) => {
    setSelectedJob(job);
    setSelectedApplications([]);
    setFilters({});
  }, []);

  const handleApplicationMove = useCallback(async (applicationId, newStage) => {
    try {
      await pipelineService?.moveCandidateToStage(applicationId, newStage);
      
      // Update local state
      setApplications(prev => 
        prev?.map(app => 
          app?.id === applicationId ? { ...app, stage: newStage } : app
        )
      );
      
      // Reload analytics
      if (selectedJob) {
        loadJobAnalytics(selectedJob?.id);
      }
    } catch (error) {
      console.error('Error moving application:', error);
    }
  }, [selectedJob]);

  const handleCandidateClick = useCallback((application) => {
    setSelectedCandidate(application);
    setShowCandidateModal(true);
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const handleBulkMove = useCallback(async (applicationIds, targetStage) => {
    if (!applicationIds?.length || !targetStage) return;
    
    try {
      await pipelineService?.bulkMoveApplications(applicationIds, targetStage);
      
      // Update local state
      setApplications(prev =>
        prev?.map(app =>
          applicationIds?.includes(app?.id) ? { ...app, stage: targetStage } : app
        )
      );
      
      setSelectedApplications([]);
      
      // Reload analytics
      if (selectedJob) {
        loadJobAnalytics(selectedJob?.id);
      }
    } catch (error) {
      console.error('Error bulk moving applications:', error);
    }
  }, [selectedJob]);

  const handleBulkReject = useCallback(async (applicationIds) => {
    if (!applicationIds?.length) return;
    
    try {
      await pipelineService?.bulkRejectApplications(applicationIds);
      
      // Update local state
      setApplications(prev =>
        prev?.map(app =>
          applicationIds?.includes(app?.id) ? { ...app, stage: 'rejected' } : app
        )
      );
      
      setSelectedApplications([]);
      
      // Reload analytics
      if (selectedJob) {
        loadJobAnalytics(selectedJob?.id);
      }
    } catch (error) {
      console.error('Error bulk rejecting applications:', error);
    }
  }, [selectedJob]);

  // Mobile keyboard navigation for stage tabs
  const onKeyDownTabs = (e) => {
    if (!stages?.length) return;
    const idx = stages?.findIndex((s) => s?.id === activeStageTab);
    if (e?.key === 'ArrowRight') {
      setActiveStageTab(stages?.[Math.min(idx + 1, stages?.length - 1)]?.id);
    } else if (e?.key === 'ArrowLeft') {
      setActiveStageTab(stages?.[Math.max(idx - 1, 0)]?.id);
    }
  };

  // DnD configuration
  const dndBackend = isTouch ? TouchBackend : HTML5Backend;
  const dndOptions = isTouch ? { enableMouseEvents: true } : undefined;

  return (
    <DndProvider backend={dndBackend} options={dndOptions}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50/50 to-gray-100/30">
        <Header />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <Breadcrumb />

            {/* Modern Page Header with Glass Morphism */}
            <div className="mb-8 bg-white/40 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-xl shadow-gray-200/20">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-2">
                    Candidate Pipeline
                  </h1>
                  <p className="text-gray-600">
                    Manage and track candidates through job-specific hiring processes
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <Button
                    variant="outline"
                    iconName="BarChart3"
                    iconPosition="left"
                    onClick={() => setShowAnalytics(true)}
                    disabled={!selectedJob}
                    className="bg-white/60 hover:bg-white/80 border-white/40 hover:border-white/60"
                  >
                    Show Analytics
                  </Button>
                  <Button 
                    variant="outline" 
                    iconName="Download" 
                    iconPosition="left"
                    className="bg-white/60 hover:bg-white/80 border-white/40 hover:border-white/60"
                  >
                    Export Data
                  </Button>
                  <Button 
                    variant="default" 
                    iconName="Plus" 
                    iconPosition="left"
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25"
                  >
                    Add Candidate
                  </Button>
                </div>
              </div>
            </div>

            {/* Job Selection */}
            <div className="mb-8">
              <JobSelector
                selectedJob={selectedJob}
                onJobSelect={handleJobSelect}
                jobs={jobs}
                jobMetrics={jobMetrics}
                loading={loading}
              />
            </div>

            {/* Pipeline Overview */}
            <PipelineOverview
              isVisible={showOverview}
              onToggle={() => setShowOverview(!showOverview)}
              overviewData={overviewData}
              loading={loading}
            />

            {/* Main Pipeline Content */}
            {selectedJob ? (
              <>
                {/* Filters */}
                <PipelineFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearFilters}
                  stages={stages}
                  jobPositions={jobs}
                  candidateSources={['LinkedIn', 'Indeed', 'Company Website', 'Referral', 'Job Board']}
                  className="mb-6"
                />

                {/* Stage Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
                  {stages?.map((stage) => {
                    const count = getApplicationsByStage(stage?.id)?.length || 0;
                    return (
                      <div
                        key={stage?.id}
                        className="bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl p-4 text-center hover:shadow-lg hover:shadow-gray-200/50 transition-all"
                        aria-label={`${stage?.name}: ${count} candidates`}
                      >
                        <div className={`w-8 h-8 ${stage?.color} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                          <span className="text-white font-bold text-sm">{count}</span>
                        </div>
                        <div className="text-xs text-gray-700 font-medium">{stage?.name}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Pipeline Board */}
                {applicationsLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {[...Array(4)]?.map((_, i) => (
                      <div key={i} className="bg-white/40 border border-white/30 rounded-2xl p-4 animate-pulse">
                        <div className="h-6 w-1/2 bg-gray-200/60 rounded mb-4" />
                        {[...Array(3)]?.map((__, j) => (
                          <div key={j} className="h-16 bg-gray-200/40 rounded-xl mb-3" />
                        ))}
                      </div>
                    ))}
                  </div>
                ) : window.innerWidth < 768 ? (
                  // Mobile Tabbed View
                  (<div className="bg-white/60 backdrop-blur-md border border-white/40 rounded-2xl shadow-xl shadow-gray-200/20">
                    <div className="border-b border-white/30" role="tablist" aria-label="Pipeline stages">
                      <nav className="flex overflow-x-auto">
                        {stages?.map((stage) => {
                          const active = activeStageTab === stage?.id;
                          const count = getApplicationsByStage(stage?.id)?.length || 0;
                          return (
                            <button
                              key={stage?.id}
                              role="tab"
                              aria-selected={active}
                              aria-controls={`panel-${stage?.id}`}
                              id={`tab-${stage?.id}`}
                              onKeyDown={onKeyDownTabs}
                              onClick={() => setActiveStageTab(stage?.id)}
                              className={`flex-shrink-0 px-4 py-4 text-sm font-medium border-b-2 transition-all ${
                                active 
                                  ? 'border-violet-500 text-violet-700 bg-violet-50/60' 
                                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50/40'
                              }`}
                            >
                              {stage?.name}({count})
                                                          </button>
                          );
                        })}
                      </nav>
                    </div>
                    <div className="p-6" id={`panel-${activeStageTab}`} role="tabpanel">
                      <div className="space-y-4">
                        {getApplicationsByStage(activeStageTab)?.length === 0 ? (
                          <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                              <span className="text-2xl">📋</span>
                            </div>
                            <p className="text-gray-600 font-medium mb-1">No candidates in this stage</p>
                            <p className="text-sm text-gray-500">Candidates will appear here as they progress through the pipeline</p>
                          </div>
                        ) : (
                          getApplicationsByStage(activeStageTab)?.map((application) => (
                            <div
                              key={application?.id}
                              onClick={() => handleCandidateClick(application)}
                              className="bg-white/80 border border-white/60 rounded-2xl p-4 cursor-pointer hover:shadow-lg hover:shadow-gray-200/40 transition-all hover:bg-white/90"
                            >
                              <div className="flex items-center gap-4">
                                <img
                                  src={application?.avatar}
                                  alt={application?.avatarAlt}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md"
                                />
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-gray-900 truncate">{application?.name}</h4>
                                  <p className="text-sm text-gray-600 truncate">{application?.email}</p>
                                  <p className="text-xs text-gray-500">{application?.location}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  {application?.score && (
                                    <div className="text-sm font-semibold text-violet-700 bg-violet-100/80 px-2 py-1 rounded-lg mb-1">
                                      {application?.score}%
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-500">
                                    {new Date(application.appliedDate)?.toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>)
                ) : (
                  // Desktop Kanban View
                  (<div className="flex gap-6 overflow-x-auto pb-6">
                    {stages?.slice(0, -1)?.map((stage) => (
                      <PipelineStage
                        key={stage?.id}
                        stage={stage}
                        candidates={getApplicationsByStage(stage?.id)}
                        onCandidateMove={handleApplicationMove}
                        onCandidateClick={handleCandidateClick}
                        onScheduleInterview={() => {}}
                        onAddNote={() => {}}
                        selectedCandidates={selectedApplications}
                        onCandidateSelect={(candidateId, selected) => {
                          setSelectedApplications(prev => 
                            selected 
                              ? [...prev, candidateId]
                              : prev?.filter(id => id !== candidateId)
                          );
                        }}
                      />
                    ))}
                  </div>)
                )}
              </>
            ) : (
              // No job selected state
              (<div className="text-center py-16">
                <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-purple-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Select a Position to Get Started
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Choose a job position above to view its candidate pipeline, manage applications, and track hiring progress.
                </p>
              </div>)
            )}
          </div>
        </main>

        {/* Analytics Modal */}
        <JobPipelineAnalytics
          isVisible={showAnalytics}
          onToggle={() => setShowAnalytics(false)}
          selectedJob={selectedJob}
          analytics={selectedJobAnalytics}
          loading={applicationsLoading}
        />

        {/* Bulk Actions */}
        <BulkActions
          selectedCandidates={selectedApplications}
          onClearSelection={() => setSelectedApplications([])}
          onBulkMove={handleBulkMove}
          onBulkEmail={() => console.log('Bulk email')}
          onBulkReject={handleBulkReject}
          stages={stages}
        />

        {/* Candidate Modal */}
        <CandidateModal
          candidate={selectedCandidate}
          isOpen={showCandidateModal}
          onClose={() => {
            setShowCandidateModal(false);
            setSelectedCandidate(null);
          }}
          onScheduleInterview={() => {}}
          onAddNote={() => {}}
        />
      </div>
    </DndProvider>
  );
};

export default CandidatePipeline;